/**
 * Game state container — server-authoritative (Phase 3, docs/ROADMAP.md).
 *
 * The engine no longer runs in the browser. Every action is a round-trip to
 * `apps/backend` via `BackendClient`; this provider never mutates game state
 * locally except for the 1 Hz display tick, which is pure prediction (smooth
 * countdowns between server syncs) and is NEVER persisted or sent anywhere —
 * the next server response always overwrites it.
 *
 * The server is mandatory: if it's unreachable, `connectionStatus` flips to
 * 'unreachable' and this provider renders `ConnectionError` instead of
 * `children`. There is no "continue offline" fallback — see the Phase 3 plan
 * for why (avoids building conflict-resolution logic a hybrid approach would
 * need).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  tick,
  type BuildingKey,
  type EngineEvent,
  type GameState,
  type ResearchKey,
  type RouletteBet,
  type ShipKey,
  type TalentPathKey,
  type TickResult,
  type TradeableResource,
  type UnitKey,
} from '@spacegame/engine';
import { ApiActionError, ApiUnreachableError, type BackendClientLike } from './backend-client';
import { captureGameError } from './sentry';
import { ConnectionError } from '../components/ConnectionError';
import { GalaxyClient } from './galaxy-client';

const LOG_LIMIT = 50;
/** How often the provider re-syncs with the server in the background, catching passive changes (sector events, returned fleets) and correcting local-tick drift. */
const DEFAULT_SYNC_INTERVAL_MS = 30_000;
/** How often a failed connection retries `getState()`. */
const RETRY_INTERVAL_MS = 5_000;

export type ConnectionStatus = 'connecting' | 'ready' | 'unreachable';

export interface AuthStatus {
  mode: 'anonymous' | 'google';
  email: string | null;
}

export interface AuthClientLike {
  getAccessToken(): Promise<string>;
  getAuthStatus(): Promise<AuthStatus>;
  linkGoogleAccount(): Promise<void>;
}

export interface GameContextValue {
  game: GameState;
  client: BackendClientLike;
  authStatus: AuthStatus | null;
  /** Rolling engine event log, newest first. */
  log: EngineEvent[];
  /** Last rejected action, for the error toast. */
  error: string | null;
  clearError: () => void;
  /**
   * THE game clock. Views must read time through this, never `Date.now()`
   * directly — otherwise the provider (injectable clock) and the views (wall
   * clock) disagree, which is invisible in production and makes every
   * time-dependent view untestable.
   */
  clock: () => number;
  /**
   * Index into `game.planets[]` the player is currently managing (galaxy
   * plan Milestone E). Building and shipyard construction are planet-aware;
   * combat and missions stay home-planet-only (Meilenstein F territory).
   * Clamped to a valid index even if a planet somehow disappeared underneath it.
   */
  activePlanetIndex: number;
  unreadMessages: number;
  refreshUnreadMessages: () => Promise<void>;
}

export interface GameActions {
  /** `planetIndex` defaults to the home planet (0) when omitted. */
  upgradeBuilding: (key: BuildingKey, planetIndex?: number) => void;
  repairBuilding: (key: BuildingKey) => void;
  cancelJob: (jobId: number) => void;
  boostJob: (jobId: number) => void;
  startResearch: (key: ResearchKey) => void;
  buildUnits: (key: UnitKey, count: number, planetIndex?: number) => void;
  interceptComet: () => void;
  shieldComet: () => void;
  harvestAsteroid: (ships: number) => void;
  harvestDebris: (ships: number) => void;
  sellResource: (resource: TradeableResource, amount: number) => void;
  buyResource: (resource: TradeableResource, amount: number) => void;
  acceptOffer: (offerId: number) => void;
  /**
   * The three casino actions and the shop purchase return the round's
   * `TickResult` directly (unlike every other action, which is
   * fire-and-forget) — callers need the exact deterministic outcome to
   * choreograph their reveal animation against, not just the eventually
   * re-rendered `log`. Now a round-trip, so callers must `await`.
   */
  playSlots: (stake: number) => Promise<TickResult | undefined>;
  playHighLow: (stake: number, guess: 'higher' | 'lower', current: number) => Promise<TickResult | undefined>;
  playRoulette: (stake: number, bet: RouletteBet) => Promise<TickResult | undefined>;
  purchaseProduct: (productId: string) => Promise<TickResult | undefined>;
  scoutCamp: (campId: number, probes: number) => void;
  attackCamp: (campId: number, fleet: Partial<Record<ShipKey, number>>) => void;
  spendTalentPoint: (path: TalentPathKey) => void;
  renameCommander: (name: string) => void;
  /** Founds a new colony (galaxy plan Milestone E) — a dedicated endpoint,
   * not part of the generic action dispatch table, since it needs a
   * backend-claimed galaxy slot. Returns the TickResult so the UI can react
   * to the new planet directly instead of waiting for the next re-render. */
  colonize: () => Promise<TickResult | undefined>;
  colonizeAt: (system: number, position: number) => Promise<TickResult | undefined>;
  abandonColony: (planetIndex: number) => Promise<TickResult | undefined>;
  attackNpcColony: (ring: number, system: number, position: number, fleet: Partial<Record<ShipKey, number>>) => Promise<TickResult | undefined>;
  scoutNpcColony: (ring: number, system: number, position: number, probes: number) => Promise<TickResult | undefined>;
  attackPlayer: (ring: number, system: number, position: number, fleet: Partial<Record<ShipKey, number>>) => Promise<TickResult | undefined>;
  scoutPlayer: (ring: number, system: number, position: number, probes: number) => Promise<TickResult | undefined>;
  /** Switches which planet `BuildingsView` manages (galaxy plan Milestone E, Teil 2). */
  setActivePlanetIndex: (index: number) => void;
  resetGame: () => void;
  /**
   * Upgrades the current anonymous session to a Google account so the same
   * save follows the player across browsers/devices. Redirects the browser
   * to Google on success — there is no further UI feedback to show here.
   */
  linkGoogleAccount: () => void;
}

const GameContext = createContext<GameContextValue | null>(null);
const ActionsContext = createContext<GameActions | null>(null);

export interface GameProviderProps {
  client: BackendClientLike;
  auth: AuthClientLike;
  /** Injectable clock for tests. */
  now?: () => number;
  /** Local display-tick interval in ms — cosmetic only, never persisted. */
  tickMs?: number;
  /** Background re-sync interval in ms. */
  syncIntervalMs?: number;
  children: ReactNode;
}

export function GameProvider({
  client,
  auth,
  now = Date.now,
  tickMs = 1000,
  syncIntervalMs = DEFAULT_SYNC_INTERVAL_MS,
  children,
}: GameProviderProps) {
  const [game, setGame] = useState<GameState | null>(null);
  const [activePlanetIndex, setActivePlanetIndex] = useState(0);
  const [log, setLog] = useState<EngineEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [authStatus, setAuthStatus] = useState<AuthStatus | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('connecting');
  const [unreadMessages, setUnreadMessages] = useState(0);
  const gameRef = useRef<GameState | null>(null);
  gameRef.current = game;

  const appendLog = useCallback((events: EngineEvent[]) => {
    if (events.length === 0) return;
    setLog((prev) => [...events.slice().reverse(), ...prev].slice(0, LOG_LIMIT));
  }, []);

  const refreshAuthStatus = useCallback(async () => {
    try {
      setAuthStatus(await auth.getAuthStatus());
    } catch {
      setAuthStatus(null);
    }
  }, [auth]);

  /** Pull the authoritative state from the server. Never throws. */
  const sync = useCallback(async () => {
    try {
      const result = await client.getState();
      setGame(result.state);
      appendLog(result.events);
      setUnreadMessages(await client.unreadMessageCount());
      setConnectionStatus('ready');
    } catch (e) {
      if (e instanceof ApiUnreachableError) {
        setConnectionStatus('unreachable');
      } else {
        captureGameError(e);
      }
    }
  }, [client, appendLog]);

  const refreshUnreadMessages = useCallback(async () => {
    try {
      setUnreadMessages(await client.unreadMessageCount());
    } catch {
      // Mailbox availability must not prevent the game from rendering.
    }
  }, [client]);

  // Initial load + auth status.
  useEffect(() => {
    void sync();
    void refreshAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, auth]);

  // Retry loop while unreachable.
  useEffect(() => {
    if (connectionStatus !== 'unreachable') return;
    const id = window.setInterval(() => void sync(), RETRY_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [connectionStatus, sync]);

  // Background re-sync while ready — catches passive server-side changes and
  // corrects local-tick drift.
  useEffect(() => {
    if (connectionStatus !== 'ready') return;
    const id = window.setInterval(() => void sync(), syncIntervalMs);
    return () => window.clearInterval(id);
  }, [connectionStatus, sync, syncIntervalMs]);

  // Resync immediately when the tab regains focus or the network comes back.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void sync();
    };
    const onOnline = () => void sync();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [sync]);

  // The 1 Hz LOCAL display tick — pure prediction, never persisted. Runs the
  // same deterministic `tick()` the server runs, purely so countdowns and
  // production counters move smoothly between real syncs; the next server
  // response always overwrites whatever this produces.
  useEffect(() => {
    if (!game || connectionStatus !== 'ready') return;
    const id = window.setInterval(() => {
      const current = gameRef.current;
      if (!current) return;
      const result = tick(current, now());
      setGame(result.state);
    }, tickMs);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game === null, connectionStatus, tickMs, now]);

  /** Runs a server round-trip; surface ActionError as the rejection toast.
   * Shared by `act` (the `/game/action` dispatch table) and `colonize`
   * (its own `/game/colonize` endpoint — it needs a backend-claimed galaxy
   * slot, which can't travel through the generic action payload). */
  const call = useCallback(
    async (run: () => Promise<TickResult>): Promise<TickResult | undefined> => {
      try {
        const result = await run();
        setGame(result.state);
        appendLog(result.events);
        setError(null);
        return result;
      } catch (e) {
        if (e instanceof ApiActionError) {
          setError(e.message);
          return undefined;
        }
        if (e instanceof ApiUnreachableError) {
          setConnectionStatus('unreachable');
          return undefined;
        }
        // Anything else is a genuine bug, not a rejected player action.
        captureGameError(e, { level: gameRef.current?.level });
        throw e;
      }
    },
    [appendLog],
  );

  const act = useCallback(
    (type: string, payload?: unknown): Promise<TickResult | undefined> => call(() => client.dispatchAction(type, payload)),
    [call, client],
  );

  const colonize = useCallback((): Promise<TickResult | undefined> => call(() => client.colonize()), [call, client]);
  const colonizeAt = useCallback(
    (system: number, position: number): Promise<TickResult | undefined> => call(() => client.colonizeAt(system, position)),
    [call, client],
  );
  const abandonColony = useCallback(
    (planetIndex: number): Promise<TickResult | undefined> => call(() => client.abandonColony(planetIndex)),
    [call, client],
  );
  const attackNpcColony = useCallback(
    (ring: number, system: number, position: number, fleet: Partial<Record<ShipKey, number>>) =>
      call(() => new GalaxyClient(auth).attackColony(ring, system, position, fleet)),
    [auth, call],
  );
  const scoutNpcColony = useCallback(
    (ring: number, system: number, position: number, probes: number) => call(() => new GalaxyClient(auth).scoutColony(ring, system, position, probes)),
    [auth, call],
  );
  const attackPlayer = useCallback(
    (ring: number, system: number, position: number, fleet: Partial<Record<ShipKey, number>>) => call(() => new GalaxyClient(auth).attackPlayer(ring, system, position, fleet)),
    [auth, call],
  );
  const scoutPlayer = useCallback(
    (ring: number, system: number, position: number, probes: number) => call(() => new GalaxyClient(auth).scoutPlayer(ring, system, position, probes)),
    [auth, call],
  );

  const actions = useMemo<GameActions>(
    () => ({
      upgradeBuilding: (key, planetIndex) => void act('upgradeBuilding', { key, planetIndex }),
      repairBuilding: (key) => void act('repairBuilding', { key }),
      cancelJob: (jobId) => void act('cancelJob', { jobId }),
      boostJob: (jobId) => void act('boostJob', { jobId }),
      startResearch: (key) => void act('startResearch', { key }),
      buildUnits: (key, count, planetIndex) => void act('buildUnits', { key, count, planetIndex }),
      interceptComet: () => void act('interceptComet', {}),
      shieldComet: () => void act('shieldComet', {}),
      harvestAsteroid: (ships) => void act('harvestAsteroid', { ships }),
      harvestDebris: (ships) => void act('harvestDebris', { ships }),
      sellResource: (resource, amount) => void act('sellResource', { resource, amount }),
      buyResource: (resource, amount) => void act('buyResource', { resource, amount }),
      acceptOffer: (offerId) => void act('acceptOffer', { offerId }),
      playSlots: (stake) => act('playSlots', { stake }),
      playHighLow: (stake, guess, current) => act('playHighLow', { stake, guess, current }),
      playRoulette: (stake, bet) => act('playRoulette', { stake, bet }),
      purchaseProduct: (productId) => act('purchaseProduct', { productId }),
      scoutCamp: (campId, probes) => void act('scoutCamp', { campId, probes }),
      attackCamp: (campId, fleet) => void act('attackCamp', { campId, fleet }),
      spendTalentPoint: (path) => void act('spendTalentPoint', { path }),
      renameCommander: (name) => void act('renameCommander', { name }),
      colonize: () => colonize(),
      colonizeAt: (system, position) => colonizeAt(system, position),
      abandonColony: (planetIndex) => abandonColony(planetIndex),
      attackNpcColony: (ring, system, position, fleet) => attackNpcColony(ring, system, position, fleet),
      scoutNpcColony: (ring, system, position, probes) => scoutNpcColony(ring, system, position, probes),
      attackPlayer: (ring, system, position, fleet) => attackPlayer(ring, system, position, fleet),
      scoutPlayer: (ring, system, position, probes) => scoutPlayer(ring, system, position, probes),
      setActivePlanetIndex: (index) => setActivePlanetIndex(index),
      resetGame: () => {
        void client
          .resetGame()
          .then((result) => {
            setLog([]);
            setGame(result.state);
            setConnectionStatus('ready');
          })
          .catch((e: unknown) => {
            if (e instanceof ApiUnreachableError) setConnectionStatus('unreachable');
          });
      },
      linkGoogleAccount: () => {
        void auth.linkGoogleAccount().catch((e: unknown) => {
          setError(e instanceof Error ? e.message : 'Google-Verknüpfung fehlgeschlagen.');
        });
      },
    }),
    [act, colonize, colonizeAt, abandonColony, attackNpcColony, scoutNpcColony, client, auth],
  );

  const value = useMemo<GameContextValue | null>(
    () =>
      game
        ? {
            game,
            client,
            log,
            error,
            clearError: () => setError(null),
            clock: now,
            authStatus,
            activePlanetIndex: Math.min(activePlanetIndex, game.planets.length - 1),
            unreadMessages,
            refreshUnreadMessages,
          }
        : null,
    [game, log, error, now, authStatus, activePlanetIndex, unreadMessages, refreshUnreadMessages],
  );

  if (connectionStatus === 'unreachable') {
    return <ConnectionError />;
  }
  if (!value) return null; // one frame while the initial sync resolves

  return (
    <GameContext.Provider value={value}>
      <ActionsContext.Provider value={actions}>{children}</ActionsContext.Provider>
    </GameContext.Provider>
  );
}

export function useGame(): GameContextValue {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error('useGame must be used inside <GameProvider>.');
  return ctx;
}

export function useGameActions(): GameActions {
  const ctx = useContext(ActionsContext);
  if (!ctx) throw new Error('useGameActions must be used inside <GameProvider>.');
  return ctx;
}
